import type { Comic as ComicType } from '@panels/shared';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ComicActions } from '../components/ComicActions.js';
import { ComicStrip } from '../components/ComicStrip.js';
import { ErrorNote, Screen, Spinner } from '../components/ui.js';
import { ApiError, getComic, getComicSummary, revealAction, setFavorite, updateSheet } from '../lib/api.js';
import { useVisiblePoll } from '../lib/usePoll.js';
import { useRoomState } from '../lib/useRoomState.js';

/**
 * How often an open comic re-checks its title (Round 2 §1.4). Only the summary
 * travels — the drawings are already here and never change once finished.
 * Reactions will ride the same poll.
 */
const COMIC_POLL_MS = 15_000;

export function Comic() {
  const { sheetId } = useParams<{ sheetId: string }>();
  const navigate = useNavigate();
  const [comic, setComic] = useState<ComicType | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Opened from the lobby after a round (solo mode): keep an eye on the room, so
  // when the host restarts co-viewing this screen joins everyone else (Round 3).
  const revealRoomId = (useLocation().state as { revealRoomId?: string } | null)?.revealRoomId;
  const room = useRoomState(revealRoomId);
  const reveal = room.state?.reveal ?? null;
  useEffect(() => {
    if (revealRoomId && reveal?.mode === 'coview') navigate(`/room/${revealRoomId}`, { replace: true });
  }, [revealRoomId, reveal?.mode, navigate]);
  const [restarting, setRestarting] = useState(false);
  const watchTogether = async () => {
    if (!revealRoomId || !sheetId) return;
    setRestarting(true);
    try {
      // Picks up at this comic, the one the host is looking at.
      room.apply(await revealAction(revealRoomId, { type: 'restart', sheetId }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start co-viewing');
      setRestarting(false);
    }
  };
  const canWatchTogether =
    !!reveal && reveal.youHost && reveal.mode !== 'coview' && !!sheetId && reveal.order.includes(sheetId);

  useEffect(() => {
    if (!sheetId) return;
    let cancelled = false;
    getComic(sheetId)
      .then((c) => !cancelled && setComic(c))
      .catch((err) => !cancelled && setError(err instanceof ApiError ? err.message : 'Could not load that comic'));
    return () => {
      cancelled = true;
    };
  }, [sheetId]);

  // Live title: merge in the summary, keeping the panels (and so the replay) as they are.
  useVisiblePoll(
    async () => {
      if (!sheetId) return;
      try {
        const summary = await getComicSummary(sheetId);
        setComic((current) => (current && current.sheetId === summary.sheetId ? { ...current, ...summary, panels: current.panels } : current));
      } catch {
        // A missed refresh changes nothing; the next one will catch up.
      }
    },
    COMIC_POLL_MS,
    !!comic,
  );

  if (error) {
    return (
      <Screen title="Comic" back={() => navigate(-1)}>
        <ErrorNote>{error}</ErrorNote>
      </Screen>
    );
  }
  if (!comic) return <Spinner label="Loading comic…" />;

  return (
    <Screen title={comic.title ?? 'Comic'} back={() => navigate(-1)} wide>
      <ComicHeader comic={comic} onChange={setComic} />
      {canWatchTogether ? (
        <button type="button" className="button primary" onClick={watchTogether} disabled={restarting}>
          Watch together from this comic
        </button>
      ) : null}
      <ComicStrip comic={comic} zoomable />
      <ComicActions comic={comic} editable onChange={setComic} />
    </Screen>
  );
}

/** The starter — and only the starter — can title the comic they began. */
export function ComicHeader({ comic, onChange }: { comic: ComicType; onChange: (c: ComicType) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(comic.title ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onChange(await updateSheet(comic.sheetId, { title: value.trim() || null }));
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that title');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="comic-header">
      {editing ? (
        <form className="inline-form" onSubmit={save}>
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Name this comic"
            maxLength={80}
            autoFocus
          />
          <button type="submit" className="button small" disabled={busy}>
            Save
          </button>
        </form>
      ) : (
        <div className="comic-title-row">
          <h2>{comic.title ?? `${comic.starter.displayName}'s comic`}</h2>
          {comic.canTitle ? (
            <button type="button" className="link-button" onClick={() => setEditing(true)}>
              {comic.title ? 'Rename' : 'Add a title'}
            </button>
          ) : null}
        </div>
      )}
      <FavoriteToggle comic={comic} onChange={onChange} />
      {comic.status === 'abandoned' ? <p className="muted small">Unfinished — someone dropped out mid-round.</p> : null}
      {error ? <p className="error-text">{error}</p> : null}
    </div>
  );
}

/** Round 2 §2.6: star a whole comic. Private — only you see your favorites. */
function FavoriteToggle({ comic, onChange }: { comic: ComicType; onChange: (c: ComicType) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = async () => {
    const next = !comic.favorite;
    setBusy(true);
    setError(null);
    onChange({ ...comic, favorite: next }); // optimistic; put back if the server says no
    try {
      await setFavorite(comic.sheetId, next);
    } catch (err) {
      onChange({ ...comic, favorite: !next });
      setError(err instanceof ApiError ? err.message : 'Could not update your favorites');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={`favorite-toggle ${comic.favorite ? 'on' : ''}`}
        onClick={toggle}
        disabled={busy}
        aria-pressed={comic.favorite}
      >
        <span aria-hidden="true">{comic.favorite ? '★' : '☆'}</span>
        {comic.favorite ? 'In your favorites' : 'Add to favorites'}
      </button>
      {error ? <p className="error-text">{error}</p> : null}
    </>
  );
}
